import {
  InputField,
  TextareaField,
  SelectFieldForId,
} from "components/Common/input-fields"
import Notify from "components/Common/notify"
import { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import Select from "react-select"
import ReactQuill from "react-quill"
import "react-quill/dist/quill.snow.css"
import {
  EmailTemplateCreate,
  EmailTemplateUpdate,
} from "redux/slice/emailTemplate/emailTemplateSlice"
import {
  EMAIL_TEMPLATE_VARIABLES,
  toDisplayToken,
  displayToStorage,
  storageToDisplay,
  collectUsedVariables,
} from "common/data/emailTemplateVariables"
import { toUpperSnakeKey } from "utilities/convertText"

const TOOLBAR_ID = "email-template-quill-toolbar"

const COUNTRY_OPTIONS = [
  { value: "PK", label: "Pakistan" },
  { value: "IN", label: "India" },
  { value: "BD", label: "Bangladesh" },
  { value: "LK", label: "Sri Lanka" },
]

const SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    // height: "34px",
    minHeight: "34px",
    backgroundColor: "#fff",
    borderRadius: "var(--bs-border-radius, 0.375rem)",
    borderColor: state.isFocused
      ? "#86b7fe"
      : "var(--bs-border-color, #ced4da)",
    boxShadow: state.isFocused
      ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
      : "none",
    "&:hover": {
      borderColor: state.isFocused
        ? "#86b7fe"
        : "var(--bs-border-color, #ced4da)",
    },
  }),
  valueContainer: base => ({ ...base, padding: "2px 8px" }),
  placeholder: base => ({ ...base, color: "#6c757d" }),
  menu: base => ({ ...base, zIndex: 5 }),
  menuPortal: base => ({ ...base, zIndex: 9999 }),
}

// Fixed set of template categories.
const CATEGORY_OPTIONS = [
  { id: "ACCOUNT", name: "Account" },
  { id: "SCHOLARSHIP", name: "Scholarship" },
  { id: "APPLICATION", name: "Application" },
  { id: "DOCUMENT", name: "Document" },
  { id: "PAYMENT", name: "Payment" },
  { id: "NOTIFICATION", name: "Notification" },
  { id: "REMINDER", name: "Reminder" },
  { id: "APPROVAL", name: "Approval" },
  { id: "REJECTION", name: "Rejection" },
  { id: "SYSTEM", name: "System" },
  { id: "ADMIN", name: "Admin" },
  { id: "OTHER", name: "Other" },
]

export default function EmailTemplateForm(props) {
  const dispatch = useDispatch()
  const quillRef = useRef(null)
  const isUpdate =
    props?.templateData && Object.keys(props?.templateData).length !== 0

  const [createData, setCreateData] = useState({
    name: "",
    key: "",
    subject: "",
    body: "",
    description: "",
    category: "",
    status: "ACTIVE",
    countries: [],
  })
  const [varMenuOpen, setVarMenuOpen] = useState(false)

  useEffect(() => {
    if (isUpdate) {
      setCreateData({
        name: props?.templateData?.name || "",
        key: props?.templateData?.key || "",
        subject: props?.templateData?.subject || "",
        // stored {{key}} -> editor [Label]
        body: storageToDisplay(props?.templateData?.body || ""),
        description: props?.templateData?.description || "",
        category: props?.templateData?.category || "",
        status: props?.templateData?.status || "ACTIVE",
        countries: Array.isArray(props?.templateData?.countries)
          ? props?.templateData?.countries
          : [],
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChangeInfo = (fieldName, value) => {
    console.log("field update", fieldName, value)
    setCreateData(prevState => ({
      ...prevState,
      [fieldName]: value,
    }))
  }

  const modules = useMemo(
    () => ({ toolbar: { container: `#${TOOLBAR_ID}` } }),
    [],
  )

  const groupedVariables = useMemo(() => {
    const groups = {}
    EMAIL_TEMPLATE_VARIABLES.forEach(v => {
      if (!groups[v.category]) groups[v.category] = []
      groups[v.category].push(v)
    })
    return groups
  }, [])

  const handleInsertVariable = key => {
    if (!key) return
    const variable = EMAIL_TEMPLATE_VARIABLES.find(v => v.key === key)
    const editor = quillRef.current && quillRef.current.getEditor()
    if (editor && variable) {
      const token = toDisplayToken(variable)
      const range = editor.getSelection(true)
      const index = range ? range.index : editor.getLength()
      editor.insertText(index, token, "user")
      editor.setSelection(index + token.length, 0)
    }
    setVarMenuOpen(false)
  }

  useEffect(() => {
    if (!varMenuOpen) return undefined
    const onDocClick = () => setVarMenuOpen(false)
    document.addEventListener("click", onDocClick)
    return () => document.removeEventListener("click", onDocClick)
  }, [varMenuOpen])

  const usedVariables = useMemo(
    () => collectUsedVariables(displayToStorage(createData.body), createData.subject),
    [createData.body, createData.subject],
  )

  const handleSubmit = () => {
    const storageBody = displayToStorage(createData.body)
    const payload = {
      name: createData.name,
      key: createData.key,
      subject: createData.subject,
      body: storageBody,
      description: createData.description,
      category: createData.category,
      // Always send ACTIVE / INACTIVE to the backend, regardless of casing
      // stored on older records.
      status: createData.status ? createData.status.toUpperCase() : createData.status,
      countries: createData.countries,
      variables: collectUsedVariables(storageBody, createData.subject),
    }
    console.log("payload", payload)

    if (!payload.name || !payload.key || !payload.subject) {
      Notify("error", "Please fill Name, Key and Subject.")
      return
    }

    if (isUpdate) {
      dispatch(EmailTemplateUpdate({ id: props.updateId, data: payload }))
        .then(resp => {
          if (resp?.payload?.status === 200) {
            Notify("success", "Email template updated")
            props.toggleTab()
          }
        })
        .catch(err => console.log("error", err))
    } else {
      dispatch(EmailTemplateCreate(payload))
        .then(resp => {
          if (resp?.payload?.status === 200 || resp?.payload?.status === 201) {
            Notify("success", "Email template created")
            props.toggleTab()
          }
        })
        .catch(err => console.log("error", err))
    }
  }

  return (
    <div className="card p-2">
      <div className="card-title fs-3 mt-2 mx-4">
        {isUpdate ? "Update Email Template" : "Create Email Template"}
      </div>
      <div className="card-body">
        <div className="row">
          <InputField
            label="Name"
            placeholder="Enter Template Name"
            className="col-sm-12 col-md-6 col-lg-4 my-2"
            compulsory={true}
            disabled={false}
            type="text"
            value={createData.name}
            onChange={e =>
              setCreateData(prev => ({
                ...prev,
                name: e.target.value,
                // key auto-derived from the name, e.g.
                // "Scholarship Application Submitted" -> "SCHOLARSHIP_APPLICATION_SUBMITTED"
                key: toUpperSnakeKey(e.target.value),
              }))
            }
          />
          {/* <InputField
            label="Key (auto-generated)"
            placeholder="Auto-generated from Name"
            className="col-sm-12 col-md-6 col-lg-4 my-2"
            compulsory={false}
            disabled={true}
            type="text"
            value={createData.key}
            onChange={() => {}}
          /> */}
          <SelectFieldForId
            label="Category"
            className="col-sm-12 col-md-6 col-lg-4 my-2"
            compulsory={true}
            disabled={false}
            selectOptions={CATEGORY_OPTIONS}
            value={createData.category}
            onChange={e => handleChangeInfo("category", e.target.value)}
          />
          <SelectFieldForId
            label="Status"
            className="col-sm-12 col-md-4 col-lg-4 my-2"
            compulsory={true}
            disabled={false}
            selectOptions={[
              { id: "ACTIVE", name: "Active" },
              { id: "INACTIVE", name: "Inactive" },
            ]}
            value={createData.status}
            onChange={e => handleChangeInfo("status", e.target.value)}
          />
          <div className="col-sm-12 col-md-6 col-lg-4 my-2">
            <label>Countries</label>
            <Select
              isMulti
              styles={SELECT_STYLES}
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : undefined
              }
              options={COUNTRY_OPTIONS}
              value={COUNTRY_OPTIONS.filter(o =>
                createData.countries?.includes(o.value),
              )}
              onChange={selected =>
                handleChangeInfo(
                  "countries",
                  selected ? selected.map(s => s.value) : [],
                )
              }
              placeholder="Select countries..."
            />
          </div>
          <InputField
            label="Subject"
            placeholder="Enter Email Subject"
            className="col-sm-12 col-md-8 col-lg-8 my-2"
            compulsory={true}
            disabled={false}
            type="text"
            value={createData.subject}
            onChange={e => handleChangeInfo("subject", e.target.value)}
          />
          <TextareaField
            label="Description"
            placeholder="Enter Description"
            className="col-sm-12 my-2"
            compulsory={false}
            disabled={false}
            rows="2"
            value={createData.description}
            onChange={e => handleChangeInfo("description", e.target.value)}
          />

          <div className="col-sm-12 my-2">
            <label className="mb-1">Body</label>
            {/* Custom Quill toolbar — the "Insert Variable" dropdown lives here */}
            <div
              id={TOOLBAR_ID}
              className="ql-toolbar ql-snow d-flex flex-wrap align-items-center"
            >
              <span className="ql-formats">
                <select className="ql-header" defaultValue="">
                  <option value="1" />
                  <option value="2" />
                  <option value="3" />
                  <option value="" />
                </select>
              </span>
              <span className="ql-formats">
                <button type="button" className="ql-bold" />
                <button type="button" className="ql-italic" />
                <button type="button" className="ql-underline" />
                <button type="button" className="ql-strike" />
              </span>
              <span className="ql-formats">
                <button type="button" className="ql-list" value="ordered" />
                <button type="button" className="ql-list" value="bullet" />
              </span>
              <span className="ql-formats">
                <button type="button" className="ql-link" />
                <button type="button" className="ql-clean" />
              </span>
              <span
                className="ql-formats"
                style={{ marginLeft: "auto", marginRight: 0, position: "relative" }}
              >
                <button
                  type="button"
                  style={{width: "120px"}}
                  className="btn btn-light p-1"
                  onMouseDown={e => e.preventDefault()}
                  onClick={e => {
                    e.stopPropagation()
                    setVarMenuOpen(o => !o)
                  }}
                  title="Insert Variable"
                >
                  Insert Variable ▾
                </button>
                {varMenuOpen && (
                  <div
                    onMouseDown={e => e.preventDefault()}
                    onClick={e => e.stopPropagation()}
                    style={{
                      position: "absolute",
                      top: "100%",
                      right: 0,
                      zIndex: 1050,
                      marginTop: "4px",
                      minWidth: "200px",
                      maxHeight: "260px",
                      overflowY: "auto",
                      background: "#fff",
                      border: "1px solid #e2e5e9",
                      borderRadius: "6px",
                      boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
                      textAlign: "left",
                    }}
                  >
                    {Object.keys(groupedVariables).map(category => (
                      <div key={category}>
                        <div
                          style={{
                            padding: "6px 12px",
                            fontSize: "11px",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.03em",
                            color: "#8a929c",
                            background: "#f6f7f9",
                          }}
                        >
                          {category}
                        </div>
                        {groupedVariables[category].map(v => (
                          <div
                            key={v.key}
                            onClick={() => handleInsertVariable(v.key)}
                            className="et-var-item"
                            style={{
                              padding: "7px 12px",
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              fontSize: "14px",
                            }}
                            onMouseEnter={e =>
                              (e.currentTarget.style.background = "#f1f5ff")
                            }
                            onMouseLeave={e =>
                              (e.currentTarget.style.background = "transparent")
                            }
                          >
                            {v.label}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </span>
            </div>
            <ReactQuill
              ref={quillRef}
              theme="snow"
              modules={modules}
              value={createData.body}
              onChange={value => handleChangeInfo("body", value)}
            />
            {usedVariables.length > 0 && (
              <div className="mt-2">
                <small className="text-muted me-2">Variables used:</small>
                {usedVariables.map(v => (
                  <span key={v.key} className="badge me-1" style={{background: "var(--theme-color-secondary)"}}>
                    {v.label}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="d-flex justify-content-end mt-4">
          <button
            className="btn btn-danger me-2"
            onClick={() => props.toggleTab()}
          >
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSubmit}>
            {isUpdate ? "Update" : "Create"}
          </button>
        </div>
      </div>
    </div>
  )
}
